// Shared "is it safe to reload right now?" registry for the app-update flow.
// No React. Module-level state. The scheduler in ./app-update.ts is the only
// caller of applyUpdateNow(); everything else only registers holds/flushers.
//
// Two levels:
//   isHardBusy()            never reload, not even when the user taps Refresh
//   isIdleForSilentApply()  safe to reload without asking
//
// Storage rule: this feature uses sessionStorage keys prefixed whw-update-
// only. Never adia-*, never localStorage (owner-safety protects those).

import { isAudioQueueProcessing } from '@/lib/audio-queue';
import { isContentSyncInFlight } from '@/lib/content-lifecycle/sync';
import { isOwnerTransitionBarrierActive } from '@/lib/owner-transition';
import { getSupabase } from '@/lib/supabase';
import { hasActiveVoiceCaptureWork } from '@/lib/voice-work-fence';

/** A focused safe-draft field counts as "typing" for this long after input. */
export const RECENT_INPUT_MS = 60_000;
/** The home banner yields to the composer for this long after a keystroke. */
export const TYPING_QUIET_MS = 8_000;
/** Upper bound on waiting for the first session resolution (stuck auth lock). */
const AUTH_BOOT_MAX_MS = 30_000;

const holds = new Map<symbol, string>();
const flushers = new Set<() => void>();
const listeners = new Set<() => void>();

let overlayDepthReader: (() => number) | null = null;
let lastUserInputAt = 0;
let lastKeyInputAt = 0;
let authBootSettled = false;
let installed = false;

function notify(): void {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch {
      // A listener must never break a hold release.
    }
  }
}

/** Re-evaluate whenever a hold, overlay depth, or typing state changes. */
export function subscribeUpdateSafety(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Tell the scheduler something it cannot observe (e.g. nav depth) changed. */
export function notifyUpdateSafetyChanged(): void {
  notify();
}

/**
 * Block every reload (silent, forced, and the Refresh button) until the
 * returned release runs. Token-based, so a double release is harmless and
 * overlapping holds with the same reason do not cancel each other.
 */
export function holdUpdate(reason: string): () => void {
  const token = Symbol(reason);
  holds.set(token, reason);
  notify();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds.delete(token);
    notify();
  };
}

/** Synchronous flushes that run immediately before a reload. */
export function registerDraftFlusher(flush: () => void): () => void {
  flushers.add(flush);
  return () => {
    flushers.delete(flush);
  };
}

/**
 * The nav coordinator lives inside NavStackProvider, so the banner (mounted
 * inside it) hands us a reader. Until one is registered the overlay depth is
 * unknown and silent apply stays closed.
 */
export function registerOverlayDepthReader(reader: () => number): () => void {
  overlayDepthReader = reader;
  notify();
  return () => {
    if (overlayDepthReader === reader) overlayDepthReader = null;
  };
}

/**
 * A sign-in return is in flight until the Supabase client's first session
 * resolution has settled. That resolution includes the client's own handling
 * of return parameters in the URL (implicit flow: #access_token; errors:
 * #error_description), so reloading before it settles could drop a sign-in.
 *
 * Deliberately NOT keyed on the URL itself: on a failed or cancelled return
 * the client leaves #error=... in the address bar for the life of the
 * document, which would otherwise block updates forever.
 */
export function isAuthReturnInProgress(): boolean {
  return getSupabase() !== null && !authBootSettled;
}

/** The hold reason useVoiceCapture registers while recording or transcribing. */
export const VOICE_HOLD_REASON = 'voice-capture';

/**
 * Recording, transcription, or unfinished voice persistence. The update line
 * hides completely for this (it must never interrupt a spoken prayer), rather
 * than showing a disabled button for minutes.
 */
export function isVoiceWorkActive(): boolean {
  if (hasActiveVoiceCaptureWork()) return true;
  for (const reason of holds.values()) {
    if (reason === VOICE_HOLD_REASON) return true;
  }
  return false;
}

export function isHardBusy(): boolean {
  return (
    holds.size > 0 ||
    hasActiveVoiceCaptureWork() ||
    isOwnerTransitionBarrierActive() ||
    isAuthReturnInProgress()
  );
}

const TEXT_INPUT_TYPES = new Set(['text', 'email', 'search', 'tel', 'url', 'number', 'password']);

function isEditable(element: Element | null): boolean {
  if (element instanceof HTMLTextAreaElement) return !element.readOnly && !element.disabled;
  if (element instanceof HTMLInputElement) {
    return TEXT_INPUT_TYPES.has(element.type) && !element.readOnly && !element.disabled;
  }
  return element instanceof HTMLElement && element.isContentEditable;
}

function isSafeDraft(element: Element): boolean {
  return element.hasAttribute('data-update-safe-draft');
}

/**
 * True when reloading could cost the user typed text or interrupt typing.
 *
 * Focus only matters while the page is visible. The home composer is
 * auto-focused on mount and its text is flushed to the draft before a reload,
 * so its focus counts only when there was recent input; any other focused
 * field always counts.
 */
function userTextAtRisk(now: number): boolean {
  if (typeof document === 'undefined') return false;
  const active = document.activeElement;
  if (document.visibilityState === 'visible' && isEditable(active) && active) {
    if (!isSafeDraft(active)) return true;
    if (now - lastUserInputAt < RECENT_INPUT_MS) return true;
  }
  const fields = document.querySelectorAll('textarea, input, [contenteditable="true"]');
  for (const field of fields) {
    if (isSafeDraft(field)) continue;
    if (field instanceof HTMLTextAreaElement) {
      if (!field.readOnly && !field.disabled && field.value !== '') return true;
    } else if (field instanceof HTMLInputElement) {
      if (isEditable(field) && field.value !== '') return true;
    } else if ((field.textContent ?? '').trim() !== '') {
      return true;
    }
  }
  return false;
}

export function isIdleForSilentApply(now: number = Date.now()): boolean {
  if (isHardBusy()) return false;
  if (overlayDepthReader === null || overlayDepthReader() > 0) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  if (isContentSyncInFlight() || isAudioQueueProcessing()) return false;
  return !userTextAtRisk(now);
}

/** The user typed within the last few seconds (home banner yields). */
export function isTypingRecently(now: number = Date.now()): boolean {
  return now - lastKeyInputAt < TYPING_QUIET_MS;
}

/** Flush registered drafts, then reload. Only the scheduler calls this. */
export function applyUpdateNow(): void {
  for (const flush of [...flushers]) {
    try {
      flush();
    } catch {
      // One failing flusher must not block the others or the reload.
    }
  }
  window.location.reload();
}

/**
 * Idempotent. Starts input tracking and the first-session watch, and returns
 * a disposer that removes every listener it added.
 */
export function installUpdateSafety(): () => void {
  if (installed || typeof window === 'undefined') return () => undefined;
  installed = true;

  const onInput = () => {
    lastUserInputAt = Date.now();
  };
  const onKeyInput = (event: Event) => {
    const now = Date.now();
    lastUserInputAt = now;
    // "Typing" means keys going into a text field. A key pressed on a button
    // (Enter on Refresh, Tab between controls) must not hide the line that
    // the user is operating from the keyboard.
    if (!(event.target instanceof Element) || !isEditable(event.target)) return;
    const wasQuiet = now - lastKeyInputAt >= TYPING_QUIET_MS;
    lastKeyInputAt = now;
    if (wasQuiet) notify();
  };
  const options: AddEventListenerOptions = { capture: true, passive: true };
  window.addEventListener('pointerdown', onInput, options);
  window.addEventListener('keydown', onKeyInput, options);
  window.addEventListener('input', onKeyInput, options);
  const dispose = () => {
    window.removeEventListener('pointerdown', onInput, options);
    window.removeEventListener('keydown', onKeyInput, options);
    window.removeEventListener('input', onKeyInput, options);
    installed = false;
  };

  const supabase = getSupabase();
  if (supabase === null) {
    authBootSettled = true;
    return dispose;
  }
  // getSession resolves only after the client finished its own URL-return
  // processing, which is exactly the moment a reload becomes safe again. The
  // timer bounds a resolution that never settles, so updates cannot be
  // blocked for the life of the document.
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  const settle = () => {
    if (settleTimer !== undefined) clearTimeout(settleTimer);
    settleTimer = undefined;
    if (authBootSettled) return;
    authBootSettled = true;
    notify();
  };
  settleTimer = setTimeout(settle, AUTH_BOOT_MAX_MS);
  supabase.auth.getSession().then(settle, settle);
  return () => {
    if (settleTimer !== undefined) clearTimeout(settleTimer);
    settleTimer = undefined;
    dispose();
  };
}
